/**
 * SEOHead — injects title, meta, OG, Twitter card, canonical, and JSON-LD tags via react-helmet-async.
 *
 * Key behaviours:
 * - og:title / twitter:title do NOT include "| Karmana" — they show the entity name alone.
 * - <title> includes "| Karmana" for the browser tab.
 * - toAbsoluteUrl converts /api/files/… relative paths to absolute HTTPS using the app base URL,
 *   so externally-served crawlers (WhatsApp, Google) can fetch the OG image.
 * - statusCode=404 injects prerender-status-code + noindex for invalid slugs.
 */
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://karmana.in';
// Base URL for resolving /api/files/… paths — baked into the build per environment.
const BASE_URL = process.env.REACT_APP_BACKEND_URL || SITE_URL;
const DEFAULT_IMAGE = `${SITE_URL}/images/logo.png`;
const DEFAULT_DESCRIPTION =
  'Karmana — Social impact platform for Bengaluru connecting changemakers, activities, and sustainable shopping.';

/** Convert a relative /api/files/… or /images/… path to an absolute HTTPS URL. */
export function toAbsoluteUrl(url) {
  if (!url) return null;
  if (url.startsWith('https://') || url.startsWith('http://')) return url;
  if (url.startsWith('/')) return `${BASE_URL}${url}`;
  return null;
}

/** Strip HTML tags and collapse whitespace for meta descriptions. */
export function stripHtml(text = '') {
  return text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Truncate to maxLen chars, cutting at last word boundary. */
export function truncateDesc(text = '', maxLen = 155) {
  const cleaned = stripHtml(text);
  if (cleaned.length <= maxLen) return cleaned;
  return cleaned.slice(0, maxLen - 1).replace(/\s\S*$/, '') + '…';
}

export default function SEOHead({
  title,           // entity name — used as-is for og:title; appended with "| Karmana" for <title>
  description,     // plain-text description
  canonical,       // full canonical URL
  ogImage,         // image URL — relative /api/files/… or absolute https://
  ogType = 'website',
  jsonLd,
  statusCode,      // pass 404 for not-found pages
  noIndex = false,
}) {
  const browserTitle = title ? `${title} | Karmana` : 'Karmana | Impact through Action';
  // og:title = just the entity name, no "| Karmana"
  const socialTitle = title || 'Karmana';
  const desc = description || DEFAULT_DESCRIPTION;
  const resolvedImage = toAbsoluteUrl(ogImage) || DEFAULT_IMAGE;
  const url = canonical || SITE_URL;
  const is404 = statusCode === 404;

  return (
    <Helmet>
      <title>{browserTitle}</title>
      <meta name="description" content={desc} />
      {(noIndex || is404) && <meta name="robots" content="noindex, nofollow" />}
      {is404 && <meta name="prerender-status-code" content="404" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="Karmana" />
      <meta property="og:image" content={resolvedImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@karmana_in" />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={resolvedImage} />

      <link rel="canonical" href={url} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
